/******************************************************************************
 * Created Date: Friday September 25th 2020                                   *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Thu Sep 02 2021                                             *
 * Modified By: Sean W.                                                       *
 * -----                                                                      *
 * File: /src/ScanResultsContextProvider.js                                   *
 * Copyright (c) 2020 Kuva                                                    *
 ******************************************************************************/

/* This context provider was created to seperate the getting of the scan results from the UI tab.
 * it watches changes in the parameters and fetches new results when needed
 * if the date is set to latest it also fetches new data every 30 seconds.
 * in addition, this component also handles adding telemetry to the scan results
 */
import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useParams, withRouter } from "react-router-dom";

import CameraAPI from "./apis/CameraAPI";

export const ScanResultsContext = createContext({});
export const useScanResults = () => useContext(ScanResultsContext);

const ScanResultsContextProvider = props => {
  const { camera } = useParams();
  const [telemetry, setTelemetry] = useState([]); // TODO: this is never used!
  const [scanResults, setScanResults] = useState([]);
  const [dataLoading, setDataLoading] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDetections, setShowDetections] = useState(false);
  const [showUIDetections, setShowUIDetections] = useState(true); //TODO: use local storage
  const [showNonDetections, setShowNonDetections] = useState(
    localStorage.getItem("showNonDetections") === "true"
  );

  const toggleShowNonDetections = val => {
    localStorage.setItem("showNonDetections", val === true);
    setShowNonDetections(val);
  };

  const [blobContainer, setBlobContainer] = useState(
    localStorage.getItem("scanResultsBlobContainer") || "scanresult"
  );

  const [minThreshold, setMinThreshold] = useState(
    localStorage.getItem("minThresh")
      ? parseInt(localStorage.getItem("minThresh"))
      : 0
  );

  /**
   * @export function getScanResults - This is called on every interval when we're looking at the latest date (live mode)
   * @param {string} camera - the device id for a camera to be fetched
   * @param {string} blobContainer - the container in which to look for results
   * @param {string} startDate - the date of the first scan to fetch in zulu time (toISOString())
   * @param {string} endDate - the date of the last scan to fetch in zulu time (toISOString())
   * @param {boolean} showDetections - whether to include detections in the results
   * @param {number} minThreshold - only include detections with confidence higher than this number
   * @param {boolean} showNonDetections - whether to include overlays that are outside the detections areas
   *
   * This function retreives the scan results from the backend according to the parameters supplied
   * On callback it checks whether we have an old result and if the first (latest) item in the old
   * array is the same one as the first item in the new array.
   * If its not the same, that means something changed in the backend (new item) so we replace the old
   * array of scan results with the new one, otherwise don't change anything.
   */
  const getLatestFrame = useCallback(() => {
    // First of all we determin that there's still a device selected
    // and we're looking at the latest results.
    if (!camera /* || endDate*/) return;
    // Make the same API call
    CameraAPI.getScanResults(
      camera,
      blobContainer,
      startDate
        ? moment(startDate).format("YYYY-MM-DD[T]HH:mm:ss:SSS[Z]")
        : null,
      endDate ? moment(endDate).format("YYYY-MM-DD[T]HH:mm:ss:SSS[Z]") : null,
      showDetections,
      minThreshold,
      showNonDetections,
      true
    )
      .then(response => {
        // clean out any duplicated data.
        setScanResults(prevSR => {
          let toUpdate = response.data;
          // if updated data exists and the latest frame id is different than the one that's showing
          // Update the UI
          if (toUpdate?.[0]?.id && prevSR?.[0]?.id !== toUpdate[0].id) {
            return toUpdate;
          } else {
            console.log("%c Nothing changed", "color:#ebd834");

            return prevSR;
          }
        });
        return;
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setDataLoading(false);
      });
  }, [
    camera,
    endDate,
    blobContainer,
    startDate,
    showDetections,
    minThreshold,
    showNonDetections,
    telemetry,
    setScanResults,
    setDataLoading
  ]);

  /**
   * @export function getScanResults
   * @param {string} camera - the device id for a camera to be fetched
   * @param {string} blobContainer - the container in which to look for results
   * @param {string} startDate - the date of the first scan to fetch in zulu time (toISOString())
   * @param {string} endDate - the date of the last scan to fetch in zulu time (toISOString())
   * @param {boolean} showDetections - whether to include detections in the results
   * @param {number} minThreshold - only include detections with confidence higher than this number
   * @param {boolean} showNonDetections - whether to include overlays that are outside the detections areas
   *
   * This function retreives the scan results from the backend according to the parameters supplied
   * On callback it sets the new scan results to the state and removes the loading spinner
   */
  const getScanResults = useCallback(() => {
    console.log("%c getScanResults called", "color:#e34f22");
    console.log("params", {
      camera,
      blobContainer,
      startDate,
      endDate,
      showDetections,
      minThreshold,
      showNonDetections,
      cancleable: true
    });

    CameraAPI.getScanResults(
      camera,
      blobContainer,
      startDate
        ? moment(startDate).format("YYYY-MM-DD[T]HH:mm:ss:SSS[Z]")
        : null,
      endDate ? moment(endDate).format("YYYY-MM-DD[T]HH:mm:ss:SSS[Z]") : null,
      showDetections,
      minThreshold,
      showNonDetections,
      true
    )
      .then(response => {
        console.log(
          `%c Filtered scan results: ${response.data.length}`,
          "color:#e34f22"
        );
        setScanResults(response.data);
        setDataLoading(false);
        // setImgLoading(false);
      })
      .catch(error => {
        console.error(error);
        //TODO: show error toast on fail
        setDataLoading(false);
      });
  }, [
    blobContainer,
    camera,
    endDate,
    minThreshold,
    showDetections,
    showNonDetections,
    startDate,
    telemetry
  ]);

  useEffect(() => {
    let interval;
    getScanResults();
    setDataLoading(true);
    if (!endDate || moment().isSame(endDate, "day")) {
      interval = setInterval(getLatestFrame, 30 * 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    camera,
    blobContainer,
    startDate,
    endDate,
    showDetections,
    minThreshold,
    showNonDetections
  ]);

  useEffect(() => {
    console.log("resetting scan results");
    setDataLoading(true);
    setScanResults([]);
  }, [camera]);

  return (
    <ScanResultsContext.Provider
      value={{
        getScanResults,
        scanResults,
        setScanResults,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        showDetections,
        setShowDetections,
        showNonDetections,
        setShowNonDetections: toggleShowNonDetections,
        dataLoading,
        blobContainer,
        setBlobContainer,
        minThreshold,
        setMinThreshold,
        showUIDetections,
        setShowUIDetections
      }}
    >
      {props.children}
    </ScanResultsContext.Provider>
  );
};

export const ScanResultsProvider = withRouter(ScanResultsContextProvider);
